<!-- eslint-disable vue/no-mutating-props -->
<template>
  <BaseCard class="music-card" :data-source="dataSource" :checked="checked">
    <template #cover>
      <div class="card-cover">
        <UploadProgress
          v-if="
            [
              EXECUTION_STATUS.ERROR,
              EXECUTION_STATUS.PROCESSING,
              EXECUTION_STATUS.DATA_INIT,
            ].includes(dataSource.status)
          "
          :dataSource="dataSource"
        >
          <template #close>
            <slot v-if="$slots?.close" name="close"></slot>
          </template>
        </UploadProgress>
        <template v-else>
          <Resource
            v-if="dataSource.type === CARD_TYPE.MUSIC"
            class="cover-img"
            :src="musicIcon"
            :alt="$t('yin-le')"
          />
          <Resource
            v-else
            class="cover-img"
            :src="soundEffectIcon"
            :alt="$t('yin-xiao')"
          />
          <slot name="cover"></slot>
          <ExpiredCard
            v-if="dataSource?.expired"
            class="absolute bottom-0 left-0 z-20"
            :expired="dataSource.expired"
          />
        </template>
      </div>
    </template>
    <template v-if="$slots?.menu" #menu>
      <slot name="menu"></slot>
    </template>
    <div class="card-body">
      <div class="content">
        <div
          class="content-body"
          :class="{ extra: dataSource?.name || dataSource?.time }"
        >
          <div
            v-if="dataSource.title"
            class="left"
            :class="{
              box:
                (!!dataSource?.description || !!dataSource?.size) &&
                !dataSource?.edit,
            }"
          >
            <EditableLabel
              v-model:inputVisible="dataSource.edit"
              class="inline-flex title"
              :label="dataSource.title"
              :class="dataSource?.description ? '!w-[80%]' : ''"
              :editable="editable"
              :maxlength="isChinese() ? 24 : 50"
              :isHover="editable"
              @change="onUpdated"
            />
          </div>

          <div
            v-if="dataSource.description && !dataSource.edit"
            class="truncate right"
          >
            <span class="description">{{ dataSource.description }}</span>
          </div>
          <div
            v-if="dataSource?.size && !dataSource.edit"
            class="right text-end"
          >
            {{ dataSource?.size }}
          </div>
        </div>
        <slot v-if="$slots.expand" name="expand"></slot>

        <div v-if="dataSource?.name || dataSource?.time" class="footer">
          <NuxtLink
            v-if="dataSource.name"
            :to="{
              name: PageKey.PERSONAL_PAGE,
              params: {
                key: dataSource?.homepage_org_id || 0,
              },
            }"
            class="footer-left"
          >
            <a-avatar
              v-if="dataSource.avatar"
              :src="dataSource.avatar"
              :size="16"
              class="avatar"
            />
            <span class="avatar-name" :title="dataSource.name">{{
              dataSource.name
            }}</span>
          </NuxtLink>
          <div v-if="dataSource?.time" class="footer-right">
            <span class="time">
              <iconpark-icon
                name="time"
                size="14"
                class="align-text-bottom"
              ></iconpark-icon>
              {{ dataSource?.time }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <slot></slot>
  </BaseCard>
</template>
<script lang="ts" setup>
import type { IEditableConfig } from '../..'
import BaseCard from '../base/index.vue'
import ExpiredCard from '../expired/index.vue'
import UploadProgress from '~/features/uploadProgress/index.vue'
import musicIcon from '~/assets/svg/music.svg?url'
import soundEffectIcon from '~/assets/svg/sound-effect.svg?url'
import { PageKey } from '~/constants/page'

const props = withDefaults(
  defineProps<{
    dataSource?: any
    editableConfig?: IEditableConfig
    checked?: boolean
  }>(),
  {
    dataSource: () => ({}),
    checked: false,
  }
)

const editable = computed(
  () =>
    !!(props?.editableConfig && Object.keys(props.editableConfig).length !== 0)
)

const resource = injectResourceLocation()

const onUpdated = (value: string) => {
  if (props.dataSource.title !== value) {
    // trackEvent(TRACK_EVENT.YOUYAN_PLATFORM_RENAME, {
    //   target: getTargetForEventTracking(
    //     COLLECTION_MAP.SOURCE,
    //     props.dataSource.resource_type || 0
    //   ),
    //   source: resource?.source,
    //   click: 1,
    // })
    props?.editableConfig?.onSave?.(value, props.dataSource)
  }
}
</script>
<style lang="scss" scoped>
.music-card {
  @apply w-full h-auto relative flex flex-col;

  .card-cover {
    @apply relative h-full w-auto overflow-hidden rounded-tr-[10px] rounded-tl-[10px] bg-back-3;

    img {
      @apply block w-full h-full border-none align-middle object-cover;
    }
  }

  .card-body {
    @apply p-[12px] w-full flex justify-center items-center flex-1;

    .content {
      @apply w-full h-full;

      .content-body {
        @apply flex justify-between w-full h-full items-center;

        &.extra {
          height: auto !important;
        }

        .left {
          @apply w-full;

          .icon {
            @apply leading-[20px] mr-[4px] align-middle;
          }

          .title {
            @apply text-14 text-font-1 font-bold;
          }
        }

        .box {
          width: 80% !important;
          padding-inline-end: 10px;
        }

        .right {
          @apply w-full;

          .description {
            @apply text-12 text-font-40;
          }
        }
      }
    }

    .footer {
      @apply flex justify-between text-12 mt-[7px] text-font-40 font-normal;

      .footer-left {
        @apply w-1/2 truncate flex-1;

        .avatar {
          @apply align-top mr-[5px];
          border: 1px solid var(--back-3);
        }

        .avatar-name {
          @apply text-12 text-font-50 truncate flex-1;
        }
      }

      .footer-right {
        .time {
          text-shadow: 0px 6px 20px rgba(18, 25, 38, 0.1);
        }
      }
    }
  }

  .status {
    @apply absolute inset-0 w-full h-full flex justify-center items-center z-10 bg-back-3 text-center;
    border-radius: 10px 10px 0px 0px;
    backdrop-filter: blur(0px);

    .message {
      @apply mt-[10px] text-14 text-font-80 font-semibold;
    }
  }
}
</style>
